import { Box, Fab, Paper, TableCell, TableContainer, Typography } from '@mui/material';
import HomeSegment from './HomeSegment';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import { useIsAdmin } from '../atoms';
import { NewsOptions} from '../atoms/news';
import { useRecoilState, useRecoilValueLoadable, useSetRecoilState } from 'recoil';
import TableView from '../framework/ui/view/table';
import { News } from '../models/News';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import React, { useState } from 'react';
import { useLogger } from '../framework/logger';
import { useConfirmDialog } from './ConfirmDialog';
import excerpts from '../utils/excerpts';
import { useGroupDescriptors } from '../atoms/portale'
import { NullableString } from '../models/ServiceDescriptor';
import { OptionsOpenType } from './HistorySegment';
import { NewsOptionsDialog } from './NewsOptionsDialog';
import { NewsViewDialog } from './NewsViewDialog';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { notaOpAtom, notaOpListAtom, notaOpOptionsState, notaOpRequestNumberState, notaOpState, useResetNotaOp } from '../atoms/notaOperativa';

export function NotaOperativaSegment() {
    const latestNotaOps = useRecoilValueLoadable(notaOpState)
    const setNotaInf = useSetRecoilState(notaOpAtom)
    const isAdmin = useIsAdmin()
    const navigate = useNavigate()
    const { t } = useTranslation()
    const setListNotaOp = useSetRecoilState<News[]>(notaOpListAtom)
    const setNotaOpRequestNumberState = useSetRecoilState<number>(notaOpRequestNumberState)
    const logger = useLogger({ key: 'nota' });
    const { openConfirm, closeConfirm } = useConfirmDialog()
    const { areaDictionary: groupsDic } = useGroupDescriptors()
    const [optionsOpen, setOptionsOpen] = useState<OptionsOpenType>({ open: false })
    const [options, setOptions] = useRecoilState(notaOpOptionsState)
    const [openNewsView, setOpenNewsView] = useState(false)
    const [index, setIndex]= useState<number>(0)
    const resetNota = useResetNotaOp()
    
    const handleAddNews = (ev: React.MouseEvent<HTMLButtonElement>) => {
        ev.preventDefault()
        ev.stopPropagation()
        resetNota()
        navigate('ednews/0/1');
    }

    const handleDelete = (item: News) => (ev: React.MouseEvent<HTMLTableCellElement>) => {
        if (!isAdmin) {
            logger.warning(t('Non hai i diritti per eseguire l\'operazione'))
            return
        }
        openConfirm({
            title: t('Cancellazione Nota informativa'),
            prompt: t('Elimino la Nota informativa?'),
            cancelText: t('Annulla'),
            confirmText: t('Cancella'),
            cancel: () => closeConfirm(),
            confirm: () => {
                api.deleteNews(item.id).then(() => setNotaOpRequestNumberState(r => r + 1))
                // console.log('Cancellazione Effettuata')
                closeConfirm()
            },
            onClose: () => closeConfirm(),
            children: (
                <>
                    <Typography component='div' sx={{ mb: 0.5, fontWeight: 'bold' }} variant='body1'>{item.title}</Typography>
                    <Typography component='div' sx={{ mb: 2 }} variant='body2'>{item.subtitle}</Typography>
                </>
            )
        })
    }

    const handleEditNews = (item: News) => (ev: React.MouseEvent<HTMLTableCellElement>) => {
        ev.preventDefault()
        ev.stopPropagation()
        setNotaInf(item)
        navigate('ednews/' + item.id + '/1');
    }
    
    const handleView = (item: News) => (ev: React.MouseEvent<HTMLTableCellElement>) => {
        ev.preventDefault()
        ev.stopPropagation()
        setOpenNewsView(true)
        setNotaInf(item)
        let list: News[] = latestNotaOps.contents
        let myIndex = list.findIndex(x => x.id === item.id)
        // console.log('myIndex', myIndex)
        setIndex(myIndex)
    }

    const handleAnnullaNewsView = () => {
        setOpenNewsView(false)
    }

    const formatDate = (d?: string) => {
        if (d) {
            const temp = d.split('.')[0].split('T')
            const t2 = temp[0].split('-')
            return `${t2[2]}/${t2[1]}/${t2[0]}`
        }
    }
    const getTitle = (item: News) => {
        return `Nota op: ${item.title} [${item.id}]`
    }
    const getAree = (value?: NullableString) => {
        if (!value) return ''
        const temp = value.split(';').filter(x => x !== '')
        const rv = temp.map(curr => groupsDic[curr]?.label)
        // console.log(`%cgetAree: ${JSON.stringify(temp)} ${JSON.stringify(rv)} %o`, logConstants.green12ptBold, groupsDic)
        return rv.join(', ')
    }

    const handleToggleOptions = (anchorEl: HTMLDivElement) => {
        setOptionsOpen(current => {
            if (current.open) {
                return { open: false }
            } else {
                return {
                    open: true,
                    anchorEl
                }
            }
        })
    }

    const handleSaveOptions = (o: NewsOptions) => {
        // console.log(`Save Options for News: %o`, o)
        setOptions(o)
        setOptionsOpen(current => ({ ...current, open: false }))
    }
    const handleAnnullaOptions = () => {
        setOptionsOpen(current => ({ ...current, open: false }))
    }

    const handleDeleteOldNews = () => {
        alert('Will delete old note')
    }

    React.useEffect(() => {
        setListNotaOp(latestNotaOps.contents ?? [])
    }, [latestNotaOps, setListNotaOp])

    return (
        <>
            <HomeSegment title='NotaOperativa' nrec={latestNotaOps.contents?.length} onOptionsToggle={handleToggleOptions}>
                <Box sx={{ position: 'relative' }}>
                    {isAdmin && <Fab size="small" color="primary" sx={{ position: 'absolute', top: 4, right: 8, zIndex: 3 }} onClick={handleAddNews}><AddIcon /></Fab>}
                    {latestNotaOps.state === 'hasValue' && latestNotaOps.contents
                        ? <Paper sx={{ mt: 0.5, flexGrow: 1 }}>
                            <TableContainer sx={{ px: 0.5 }}>
                                <TableView
                                    items={latestNotaOps.contents}
                                    itemKey={(item: News) => item.id}
                                    thDefs={<>
                                        <TableCell><Typography color='primary'>{t('Titolo')}</Typography></TableCell>
                                        <TableCell><Typography color='primary' >{t('Sotto titolo')}</Typography></TableCell>
                                        <TableCell><Typography color='primary' >{t('Testo')}</Typography></TableCell>
                                        <TableCell><Typography color='primary' >{t('Valido fino a')}</Typography></TableCell>
                                        <TableCell><Typography color='primary' >{t('Aree')}</Typography></TableCell>
                                        <TableCell><Typography color='primary' >{t('Importante')}</Typography></TableCell>
                                        {isAdmin &&
                                            <TableCell sx={{ textAlign: 'center' }}><Typography color='primary'>{t('Azioni')}</Typography></TableCell>
                                        }
                                    </>}
                                    trDefs={(row: News) => (<>
                                        <TableCell onClick={handleView(row)} sx={{ cursor: 'pointer', color: (theme) => theme.palette.secondary.dark, fontWeight: 'bold' }} title={getTitle(row)}>{row.title}</TableCell>
                                        <TableCell>{row.subtitle}</TableCell>
                                        <TableCell>{excerpts(row.body, { words: 40 })}</TableCell>
                                        <TableCell><Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>{formatDate(row.validUntil)}{row.expired && <AccessTimeIcon fontSize='small' />}</Box></TableCell>
                                        <TableCell>{getAree(row.aree)}</TableCell>
                                        <TableCell sx={{ textAlign: 'center' }}>{row.important ? 
                                            <PriorityHighIcon color='primary'/> : null
                                            }
                                        </TableCell>
                                        {isAdmin &&
                                            <TableCell sx={{ textAlign: 'center' }}>
                                                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', textAlign: 'center', height: '100%', pt: '6px', pb: 0}}>
                                                    <Box onClick={handleDelete(row)} sx={{width: 20, height: 20}}> 
                                                        <DeleteIcon fontSize='small' titleAccess={t('Elimina')} color='primary' />
                                                    </Box>
                                                    <Box onClick={handleEditNews(row)} sx={{width: 20, height: 20}}> 
                                                        <EditIcon fontSize='small' titleAccess={t('Modifica')} color='primary' />
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                        }
                                    </>)}
                                />
                            </TableContainer>
                        </Paper>
                        : <Typography>Loading...</Typography>
                    }
                </Box>
            </HomeSegment>
            <NewsOptionsDialog
                open={optionsOpen.open}
                anchorEl={optionsOpen.anchorEl as HTMLElement}
                options={options}
                onSave={handleSaveOptions}
                onAnnulla={handleAnnullaOptions}
                onDelete={handleDeleteOldNews}
                labelView="Visualizza Nota informative Scadute"
                labelDelete="Cancelllare note scadute?"
            />
            <NewsViewDialog
                open={openNewsView}
                listNews={latestNotaOps.contents}
                index={index}
                onHandleCloseViewNews={handleAnnullaNewsView}
                atom={notaOpAtom}
            />
        </>
    )
}

export default NotaOperativaSegment;
